import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DraftInfoModal.module.css';
import DraftBankingDetailInfoComponent from './DraftBankingDetailInfoComponent';
import DraftOrganoidDetailInfoComponent from './DraftOrganoidDetailInfoComponent';
import DraftPatientDetailInfoComponent from './DraftPatientDetailInfoComponent';
import DraftCommentComponent from '../subComponets/DraftCommentComponent';
import EditBanking from '../../components/modal/EditBanking';

const CATEGORY_LABELS = {
    organoid: '오가노이드',
    stock: '뱅킹 스탁',
    patient: '환자 정보',
    image: '이미지',
};

const AUTH_LEVEL_LABELS = {
    1: '읽기', 2: '추가', 3: '수정', 4: '삭제', 5: '반입', 6: '반출',
};

const STATUS_LABELS = {
    approved: '승인', pending: '대기중', canceled: '취소', rejected: '반려',
};

// 일시적 고정 승인자 화이트리스트 (백엔드와 동일하게 유지할 것)
// [2026-09-14] 전체 승인 권한의 하한. 백엔드 managemnet_info.js 와 같은 값으로 유지할 것.
const APPROVE_MIN_AUTH_LEVEL = 4;

// 백엔드 managemnet_info.js 의 FORCE_APPROVERS 와 반드시 같게 유지할 것.
//   [2026-09-14] junsoodark@gmail.com(개인 체크용 계정) 제거하고 회사 계정으로 교체.
const FORCE_APPROVERS = [
    'mspark@gradiantbio.com',     // 박미선 팀장
    'junsoodark@gradiantbio.com', // 박준수
];

const postJson = (url, body) => fetch(url, {
    method: 'post',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
}).then(r => r.json());

const getCategoryLabelKey = (category) => {
    if (!category) return null;
    if (category === 'Organoid_Info' || category.toLowerCase().includes('organoid')) return 'organoid';
    if (category === 'Banking_Info' || category.toLowerCase().includes('stock')) return 'stock';
    if (category === 'Patient_Info' || category.toLowerCase().includes('patient')) return 'patient';
    if (category === 'Light_Microscope_picture_data' || category === 'Pathology_Picture_picture_data') return 'image';
    return null;
};

const formatDateTime = (dateTimeString) =>
    new Date(dateTimeString).toLocaleString('ko-KR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });

const hashmark = (str) => str ? str.replace('hashmark', '#') : str;

function StatusBadge({ status, t }) {
    if (!status) return null;
    return (
        <span className={`${styles.approvalIcon} ${styles[status] || ''}`}>
            {STATUS_LABELS[status] ? t(STATUS_LABELS[status]) : ''}
        </span>
    );
}

function DraftInfoModal({ show, onClose, selectedDraft, reDrawView }) {
    const { t } = useTranslation();

    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [visibleEditBankingModal, setVisibleEditBankingModal] = useState(false);
    const [userName, setUserName] = useState('');
    const [userInfo, setUserInfo] = useState({});

    const closeEditBankingModal = () => setVisibleEditBankingModal(false);

    useEffect(() => {
        postJson('/api/getLoginedUserInfo', undefined).then(data => {
            setUserName(data.data[0]?.userName);
            setUserInfo(data?.data[0]);
        });
    }, []);

    const fetchComments = async () => {
        if (!selectedDraft?.draftDocumentId) return;
        try {
            const res = await fetch('/api/getComments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ draftDocumentId: selectedDraft.draftDocumentId }),
            });
            if (!res.ok) throw new Error('댓글을 불러오는 데 실패했습니다.');
            setComments(await res.json());
        } catch (err) {
            console.error('댓글 목록 조회 실패:', err);
        }
    };

    useEffect(() => {
        if (!show) {
            document.body.style.overflow = 'unset';
            return;
        }
        fetchComments();
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'unset'; };
    }, [show, selectedDraft?.draftDocumentId]);

    const onSubmitComment = async () => {
        const trimmed = newComment.trim();
        if (!trimmed) return;
        try {
            const res = await fetch('/api/postComment', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    draftDocumentId: selectedDraft.draftDocumentId,
                    content: trimmed,
                    author: userInfo.email,
                    authorName: userInfo.userName,
                }),
            });
            if (!res.ok) {
                const errJson = await res.json();
                throw new Error(errJson.message || '댓글 등록 실패');
            }
            setNewComment('');
            await fetchComments();
        } catch (error) {
            console.error('댓글 제출 중 오류:', error);
            alert(error.message);
        }
    };

    const performDraftAction = (confirmKey, url, body) => {
        if (!window.confirm(t(confirmKey))) return;
        postJson(url, body).then(data => {
            alert(data.message);
            onClose();
            reDrawView();
        });
    };

    const cancelDraft = (id) => performDraftAction('기안을 취소하시겠습니까?', '/api/cancelDraft', { draftDocumentId: id });
    const rejectDraft = (id) => performDraftAction('반려하시겠습니까?', '/api/authorizeDraft', { draftDocumentId: id, decision: 'rejected' });
    const approveDraft = (id) => performDraftAction('승인하시겠습니까?', '/api/authorizeDraft', { draftDocumentId: id, decision: 'approved' });

    if (!show) return null;

    const categoryKey = getCategoryLabelKey(selectedDraft.draftCategory);
    const isPending = selectedDraft?.authorizedStatus === 'pending';
    // 임시 변경
    // const isApprover = userName === selectedDraft?.authorizedPersonName
    //     || userInfo.authLevel === 3
    //     || FORCE_APPROVERS.includes(userInfo.email);

    // 반출(Export) 기안 여부 판별
    // [2026-09-14] 백엔드 authorizeDraft 의 권한 조건과 정확히 같게 맞췄다.
    //   배정된 결재자 · 전체 승인 권한(>= 4) · 고정 승인자 화이트리스트.
    //   예전에는 반출(Export) 기안만 화이트리스트 두 계정으로 제한했는데,
    //   서버는 그 제한을 강제하지 않아 "버튼은 안 보이는데 API 로는 통과" 였고,
    //   정작 배정된 팀장님은 승인 버튼을 못 봤다. 반출도 다른 기안과 같은 규칙으로 간다.
    //   이름 비교는 안전하다 — User_Auth.userName 에 UNIQUE 제약이 있다.
    const isApprover = userName === selectedDraft?.authorizedPersonName
        || Number(userInfo.authLevel) >= APPROVE_MIN_AUTH_LEVEL
        || FORCE_APPROVERS.includes(userInfo.email);

    const isDrafter = selectedDraft.draftUserName && selectedDraft.draftUserName === userName;

    // 버튼 그룹 표시 조건: 결재자이거나 기안자일 때만 (그리고 대기중일 때만)
    const showButtonGroup = isPending && (isApprover || isDrafter);

    return (
        <div className={styles.popupBackground} onClick={onClose}>
            <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.draftInfoLeftTab}>
                    <div className={styles.draftInfoTitle}>
                        {categoryKey && <span>{t(CATEGORY_LABELS[categoryKey])}</span>}
                        {selectedDraft.authLevel && (
                            <span>&nbsp;{t(AUTH_LEVEL_LABELS[selectedDraft.authLevel] || '')}</span>
                        )}
                        &nbsp;{t('신청')}
                        {visibleEditBankingModal && (
                            <EditBanking onClose={closeEditBankingModal} selectedDraft={selectedDraft} />
                        )}
                        <StatusBadge status={selectedDraft?.authorizedStatus} t={t} />
                    </div>

                    <div className={styles.draftInfoDiv}>
                        <div className={styles.draftInfoColumn}>{t('기안 ID')}</div>
                        <div className={styles.draftInfoData}>{selectedDraft.draftDocumentId}</div>
                        <div className={styles.draftInfoColumn}>{t('날짜/시간')}</div>
                        <div className={styles.draftInfoData}>{formatDateTime(selectedDraft.regTime)}</div>
                        <div className={styles.draftInfoColumn}>{t('기안 타겟')}</div>
                        <div className={styles.draftInfoData}>{hashmark(selectedDraft.targetKey)}</div>
                        <div className={styles.draftInfoColumn}>{t('분류')}</div>
                        <div className={styles.draftInfoData}>{t(selectedDraft.draftCategory)}</div>
                        <div className={styles.draftInfoColumn}>{t('이름')}</div>
                        <div className={styles.draftInfoData}>{selectedDraft.draftUserName}</div>
                        <div className={styles.draftInfoColumn}>{t('팀명')}</div>
                        <div className={styles.draftInfoData}>{selectedDraft.draftUserTeamName}</div>
                        <div className={styles.draftInfoColumn}>{t('기안 의견')}</div>
                        <div className={styles.draftInfoData}>
                            {selectedDraft.draftOpinion?.split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                    {line}
                                    <br />
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {categoryKey === 'organoid' && (
                        <DraftOrganoidDetailInfoComponent
                            targetKey={selectedDraft.targetKey}
                            selectedDraft={selectedDraft}
                        />
                    )}
                    {categoryKey === 'stock' && (
                        <DraftBankingDetailInfoComponent
                            value={selectedDraft.targetKey}
                            selectedDraft={selectedDraft}
                        />
                    )}
                    {categoryKey === 'patient' && (
                        <DraftPatientDetailInfoComponent
                            targetKey={selectedDraft.targetKey}
                            selectedDraft={selectedDraft}
                        />
                    )}
                </div>

                <div className={styles.draftInfoRightTab}>
                    <button className={styles.closeButton} onClick={onClose}>&times;</button>
                    <div className={styles.approval}>승인</div>
                    <div className={styles.authorizedPersonInfo}>
                        <span>{selectedDraft?.authorizedPersonName}</span>
                        <StatusBadge status={selectedDraft?.authorizedStatus} t={t} />
                    </div>
                    <div className={styles.authTeamName}>{selectedDraft?.authorizedPersonTeamName}</div>

                    <div className={styles.commentBox}>
                        <div className={styles.commentHeader}>댓글</div>
                        <div className={styles.commentList}>
                            {comments.length > 0 ? (
                                comments.map(c => (
                                    <DraftCommentComponent
                                        key={c.id ?? c.createdAt}
                                        commentInfo={c}
                                        reDrawView={fetchComments}
                                    />
                                ))
                            ) : (
                                <div className={styles.noComments}>등록된 댓글이 없습니다.</div>
                            )}
                        </div>
                        <div className={styles.commentFooter}>
                            <textarea
                                className={styles.commentInput}
                                placeholder={t('댓글을 입력하세요...')}
                                value={newComment}
                                onChange={e => setNewComment(e.target.value)}
                            />
                            <button className={styles.commentSubmitButton} onClick={onSubmitComment}>
                                작성
                            </button>
                        </div>
                    </div>

                    {/* 버튼 그룹: 표시할 버튼이 있을 때만 div 렌더링 (빈 박스 방지) */}
                    {showButtonGroup && (
                        <div className={styles.draftButtonGroup}>
                            {isApprover ? (
                                <>
                                    <button className={styles.editButton} onClick={() => setVisibleEditBankingModal(true)}>{t('수정')}</button>
                                    <button className={styles.approvalButton} onClick={() => approveDraft(selectedDraft.draftDocumentId)}>{t('승인')}</button>
                                    <button className={styles.rejectButton} onClick={() => rejectDraft(selectedDraft.draftDocumentId)}>{t('반려')}</button>
                                    <button className={styles.cancelButton} onClick={() => cancelDraft(selectedDraft.draftDocumentId)}>{t('취소')}</button>
                                </>
                            ) : (
                                <>
                                    <button className={styles.editButton} onClick={() => setVisibleEditBankingModal(true)}>{t('수정')}</button>
                                    <button className={styles.cancelButton} onClick={() => cancelDraft(selectedDraft.draftDocumentId)}>{t('취소')}</button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DraftInfoModal;